import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';

import {
  ActionButton,
  ButtonModel,
  DrawerContext,
} from '@notino/react-styleguide';
import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import { NUSHOP_PORTAL_ROOT_ID } from '@lib/constants';
import { AllReviewPhotos } from '@lib/reviews/AllReviewPhotos';
import { useLoggedInUserReviewFetch } from '@queries/useReviews/useLoggedInUserReviewFetch';
import {
  hasImageReviewsFromData,
  useReviewsFetch,
} from '@queries/useReviews/useReviewsFetch';
import { useReviewsSummaryFetch } from '@queries/useReviews/useReviewsSummaryFetch';
import { useTabSwitcher } from '@sharedComponents/TabSwitcher/context/TabSwitcherContext';

import { useProductDetailContext } from '../../../ProductDetailContext';

import { LoadingWrapper } from './Loading/LoadingWrapper';
import { ReviewsSkeleton } from './Loading/ReviewsSkeleton';
import { ReviewSummarySkeleton } from './Loading/ReviewSummarySkeleton';
import { messages } from './messages';
import { ReviewsItems } from './ReviewItems';
import {
  createReviewFiltersStore,
  ReviewFiltersContextProvider,
} from './ReviewItems/SortFilterBar/Stores/filtersStore';
import { SortBox } from './ReviewItems/styled';
import { ReviewsSummary } from './ReviewsSummary';
import { CenterWrapper, ErrorMessage, Wrapper } from './styled';
import { shouldOpenReviewModal } from './utils/shouldOpenReviewModal';

interface IReviewsProps {
  variant: NonNullable<
    GetCatalogProductViewQuery['productDetailByCatalogMasterId']
  >['variants'][number];
  productCode: string;
  masterId: string;
  reviewsTabId: number;
  renderReviewsOnServer: boolean;
  wrapperRef?: React.Ref<HTMLDivElement>;
}

const ReviewsContent: React.FC<IReviewsProps> = React.memo(
  ({
    productCode,
    masterId,
    reviewsTabId,
    renderReviewsOnServer,
    wrapperRef,
  }) => {
    const {
      product: { catalogMasterId },
    } = useProductDetailContext();

    const { search } = useLocation();
    const reviewModalOpen = shouldOpenReviewModal(search);

    const { activeTabId } = useTabSwitcher();
    const isActive = activeTabId === reviewsTabId || Boolean(reviewModalOpen);

    const skip = renderReviewsOnServer ? false : !isActive;
    const ssr = renderReviewsOnServer ? true : isActive;

    const {
      data: reviewsSummaryData,
      loading: reviewsSummaryLoading,
      error: reviewsSummaryError,
      refetch: reviewsSummaryRefetch,
    } = useReviewsSummaryFetch(catalogMasterId, { skip, ssr });

    const {
      data: loggedInUserReviewData,
      loading: loggedInUserReviewLoading,
      refetch: refetchLoggedInUserReview,
    } = useLoggedInUserReviewFetch(productCode, {
      ssr,
      skip,
    });

    const { data, error, loading, refetch, fetchMore } = useReviewsFetch(
      productCode,
      { skip, ssr }
    );

    if (!isActive && !renderReviewsOnServer) {
      return null;
    }

    const reviewOverview =
      reviewsSummaryData?.productDetailByCatalogMasterId?.reviewOverview;
    const reviewSummary =
      reviewsSummaryData?.productDetailByCatalogMasterId?.reviewSummary;

    const productReviewCount = reviewOverview?.count ?? 0;

    const hasImageReviews = hasImageReviewsFromData(data);

    const refetchSummary = async () => {
      await reviewsSummaryRefetch();
    };

    const refetchReviews = async () => {
      await refetch();
    };

    return (
      <Wrapper ref={wrapperRef} id="pdReviews">
        <LoadingWrapper
          isLoading={reviewsSummaryLoading || loggedInUserReviewLoading}
          isError={!reviewSummary || !!reviewsSummaryError}
          skeleton={<ReviewSummarySkeleton />}
          error={
            <CenterWrapper>
              <ErrorMessage>
                <FormattedMessage {...messages.reviewsSummaryFailed} />
              </ErrorMessage>

              <ActionButton
                onClick={refetchSummary}
                buttonSize={ButtonModel.Sizes.small}
              >
                <FormattedMessage {...messages.tryAgain} />
              </ActionButton>
            </CenterWrapper>
          }
        >
          <ReviewsSummary
            reviewSummary={reviewSummary!}
            productCode={productCode}
            masterId={masterId}
            catalogMasterId={catalogMasterId}
            productReviewCount={productReviewCount}
            refetchLoggedInUserReview={async () => {
              await refetchLoggedInUserReview({
                masterProductCode: productCode,
              });
            }}
            loggedInUserReview={loggedInUserReviewData?.loggedInUserReview}
          />
        </LoadingWrapper>

        {hasImageReviews && <AllReviewPhotos />}

        <LoadingWrapper
          isLoading={loading}
          isError={!data || !!error}
          skeleton={<ReviewsSkeleton />}
          error={
            <>
              <SortBox />
              <CenterWrapper>
                <ErrorMessage>
                  <FormattedMessage {...messages.reviewsFailed} />
                </ErrorMessage>
                <ActionButton
                  onClick={refetchReviews}
                  buttonSize={ButtonModel.Sizes.small}
                >
                  <FormattedMessage {...messages.tryAgain} />
                </ActionButton>
              </CenterWrapper>
            </>
          }
        >
          <ReviewsItems
            productCode={productCode}
            reviewsAndFilters={data?.reviewsAndFilters ?? null}
            reviewsLoading={loading}
            reviewsError={error!}
            refetch={refetch}
            fetchMore={fetchMore}
            productReviewCount={productReviewCount}
          />
        </LoadingWrapper>
      </Wrapper>
    );
  }
);

export const Reviews: React.FC<IReviewsProps> = React.memo((props) => {
  const [store] = React.useState(() => createReviewFiltersStore());

  return (
    <ReviewFiltersContextProvider value={{ store }}>
      <DrawerContext.Provider
        mountElement={
          // mount it to the root not to cause z-index issues
          typeof document !== 'undefined'
            ? document.getElementById(NUSHOP_PORTAL_ROOT_ID) ?? undefined
            : undefined
        }
      >
        <ReviewsContent {...props} />
      </DrawerContext.Provider>
    </ReviewFiltersContextProvider>
  );
});
